/**
 * Formatting and status vocabulary for the Email Finder mini.
 *
 * Both panes used to carry their own copy of a twenty line date builder that
 * zero padded every part by hand, and the scan list painted its own literal
 * hex for each status - #aa0ce8 for complete, #5ce30d for running - which
 * belonged to no palette and read as a fairground next to the rest of HQ.
 * One module now answers both questions.
 */

/**
 * HQ-API sends two shapes for a moment in time. `createdAt` is a plain
 * `Y-m-d H:i:s` string and `startTime` / `endTime` are PHP's serialised
 * DateTime, `{ date, timezone_type, timezone }`. Both are read as wall clock
 * time, which is what the screen showed before and keeps a scan's created and
 * started stamps agreeing with each other.
 */
export type ApiDate = string | { date: string } | Date | null | undefined;

const MONTHS: string[] = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function toDate(value: ApiDate): Date | null {
    if (value === null || value === undefined) {
        return null;
    }
    const raw: string | Date = typeof value === 'object' && 'date' in value ? value.date : value;
    const date: Date = raw instanceof Date ? raw : new Date(String(raw).replace(' ', 'T'));

    return Number.isNaN(date.getTime()) ? null : date;
}

function pad(value: number): string {
    return value < 10 ? '0' + value : String(value);
}

/** "1 Jul 2026, 09:16" - short enough for a list row, unambiguous about the month. */
export function formatDateTime(value: ApiDate): string {
    const date: Date | null = toDate(value);
    if (date === null) {
        return 'N/A';
    }

    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, `
        + `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "12 Jun, 15:25" - for a grid whose rows all sit inside one scan's day. */
export function formatDateTimeShort(value: ApiDate): string {
    const date: Date | null = toDate(value);
    if (date === null) {
        return 'N/A';
    }

    return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "09:16:54" - the second matters when you are watching a scan run. */
export function formatTime(value: ApiDate): string {
    const date: Date | null = toDate(value);
    if (date === null) {
        return 'N/A';
    }

    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** How long a scan took, or how long it has been going. */
export function formatDuration(start: ApiDate, end: ApiDate): string {
    const from: Date | null = toDate(start);
    const to: Date | null = toDate(end);
    if (from === null || to === null) {
        return 'N/A';
    }
    const seconds: number = Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000));
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes: number = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `${minutes}m ${pad(seconds % 60)}s`;
    }

    return `${Math.floor(minutes / 60)}h ${pad(minutes % 60)}m`;
}

/** A url with the scheme and any trailing slash taken off, for a cramped row. */
export function shortUrl(url: string | undefined): string {
    if (!url) {
        return '';
    }

    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/**
 * A status tone, in the three parts a chip needs. Every value is a `--wf-`
 * token, so the chip swaps with the theme instead of staying bright green on
 * a petrol field.
 */
export interface StatusTone {
    fill: string;
    ink: string;
    line: string;
}

const NEUTRAL: StatusTone = {
    fill: 'var(--wf-surface-2)',
    ink: 'var(--wf-ink-dim)',
    line: 'var(--wf-line-strong)',
};

const OK: StatusTone = { fill: 'var(--wf-ok-soft)', ink: 'var(--wf-ok)', line: 'var(--wf-ok)' };
const BAD: StatusTone = { fill: 'var(--wf-bad-soft)', ink: 'var(--wf-bad)', line: 'var(--wf-bad)' };
const INFO: StatusTone = { fill: 'var(--wf-info-soft)', ink: 'var(--wf-info)', line: 'var(--wf-info)' };
const LIVE: StatusTone = {
    fill: 'var(--wf-live-soft)',
    ink: 'var(--wf-live-line)',
    line: 'var(--wf-live-line)',
};

/**
 * Three vocabularies share this map: a scan's status, a result's status, and
 * the operator's own activity status.
 * `running` and `pending` take the live yellow, which everywhere else in the
 * admin means work in progress, so a scan in flight reads the same as a
 * running workflow.
 */
const TONES: Record<string, StatusTone> = {
    complete: OK,
    completed: OK,
    running: LIVE,
    in_progress: LIVE,
    pending: LIVE,
    queued: LIVE,
    cancelled: NEUTRAL,
    failed: BAD,
    error: BAD,
    new: OK,
    existing: INFO,
};

export function statusTone(status: string | undefined): StatusTone {
    return TONES[String(status ?? '').toLowerCase()] ?? NEUTRAL;
}

/**
 * The email finder's url rows reach a complete state under two different words.
 * `webscraper.py` marks a url `completed` when it finishes one on its own, and
 * the parallel workflow's `web_scraper_parallel_helper_node.py` marks a whole
 * batch `complete`. Both writers are live and both write to `scraper_urls`, so
 * anything reading that column has to accept either word. HQ-19.
 */
export function isCompleteStatus(status: string | undefined): boolean {
    const value: string = String(status ?? '').toLowerCase();

    return value === 'complete' || value === 'completed';
}

/** A scan that is still working, and so worth polling for. */
export function isScanActive(status: string | undefined): boolean {
    const value: string = String(status ?? '').toLowerCase();

    return value === 'running' || value === 'pending' || value === 'queued';
}
